import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import './AdminDashboard.css';
import './AdminManagePurchases.css';
import { doc, deleteDoc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { PackageIcon } from "../components/SvgIcons";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  fetchAllPurchasesWithPseudo,
  updatePurchaseStatus,
  updatePurchaseQueuePositions,
} from "../logic/AdminPurchaseLogic";
import { toast } from 'react-toastify';

const STATUS = {
  Pending: "Pending",
  Shipped: "Shipped",
  Delivered: "Delivered",
  Cancelled: "Cancelled",
};

const DEFAULT_ITEM_COLLECTION = "shopItems-v2";

const getItemKey = (purchase) => {
  const col = (purchase?.collection || DEFAULT_ITEM_COLLECTION).toString();
  const id = (purchase?.itemId || "").toString();
  if (!col || !id) return null;
  return `${col}::${id}`;
};

const getItemDocRef = (purchase) => {
  const col = (purchase?.collection || DEFAULT_ITEM_COLLECTION).toString();
  const id = (purchase?.itemId || "").toString();
  if (!col || !id) return null;
  return doc(db, col, id);
};

const pickItemImageUrl = (itemData) => {
  if (!itemData) return null;
  return (
    itemData.imageUrl ||
    itemData.image ||
    itemData.imgUrl ||
    itemData.thumbnailUrl ||
    null
  );
};

const pickFirstTimeValue = (purchase) => {
  if (!purchase) return null;
  return (
    purchase.createdAt ||
    purchase.timestamp ||
    purchase.purchasedAt ||
    purchase.date ||
    purchase.time ||
    null
  );
};

const toMillis = (maybeTimestamp) => {
  if (!maybeTimestamp) return null;
  if (typeof maybeTimestamp === "number") return maybeTimestamp;
  if (typeof maybeTimestamp === "string") {
    const ms = Date.parse(maybeTimestamp);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof maybeTimestamp?.toDate === "function") {
    try {
      return maybeTimestamp.toDate().getTime();
    } catch {
      return null;
    }
  }
  if (typeof maybeTimestamp?.seconds === "number") return maybeTimestamp.seconds * 1000;
  return null;
};

const formatPurchaseTime = (purchase) => {
  const ms = toMillis(pickFirstTimeValue(purchase));
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "";
  }
};

const matchesSearch = (purchase, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    purchase?.pseudo,
    purchase?.itemName,
    purchase?.userId,
    purchase?.id,
    purchase?.status,
  ]
    .filter(Boolean)
    .join(" | ")
    .toLowerCase();
  return hay.includes(q);
};

function SortableQueueItem({
  purchase,
  index,
  selected,
  onSelect,
  onToggleSelected,
  onStatusChange,
  imageUrl,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: purchase.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`queue-item ${selected ? "selected" : ""}`}
      onClick={() => onSelect(purchase.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect(purchase.id);
      }}
    >
      <div
        className="queue-handle"
        title="Drag to reorder"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        ≡
      </div>

      <div className="queue-thumb" aria-hidden="true">
        {imageUrl ? (
          <img src={imageUrl} alt="" loading="lazy" />
        ) : (
          <div className="queue-thumb-placeholder" />
        )}
      </div>

      <div className="queue-index">{index + 1}</div>

      <div className="queue-main">
        <div className="top">
          <span className="pseudo">{purchase.pseudo}</span>
          <span className="meta">
            {purchase.itemName} · Qty {purchase.quantity} · {purchase.totalCost} DP
          </span>
        </div>
        <div className="meta">
          {formatPurchaseTime(purchase) ? `Purchased: ${formatPurchaseTime(purchase)}` : ""}
        </div>
      </div>

      <div className="queue-actions" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={purchase.__checked || false}
          onChange={() => onToggleSelected(purchase.id)}
          aria-label="Select purchase"
          title="Select"
        />
        <select
          value={purchase.status}
          onChange={(e) => onStatusChange(purchase.userId, purchase.id, e.target.value)}
          aria-label="Update status"
        >
          <option value={STATUS.Pending}>{STATUS.Pending}</option>
          <option value={STATUS.Shipped}>{STATUS.Shipped}</option>
          <option value={STATUS.Delivered}>{STATUS.Delivered}</option>
          <option value={STATUS.Cancelled}>{STATUS.Cancelled}</option>
        </select>
      </div>
    </div>
  );
}

function SortableUserGroup({
  group,
  groupIndex,
  selected,
  onSelectGroup,
  onSelectItem,
  onToggleItemSelected,
  onToggleGroupSelected,
  onStatusChange,
  getImageUrl,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.userId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  const totalDp = group.items.reduce((sum, p) => sum + Number(p.totalCost || 0), 0);
  const allSelected = group.items.length > 0 && group.items.every((p) => p.__checked);
  const anySelected = group.items.some((p) => p.__checked);

  return (
    <div ref={setNodeRef} style={style} className={`user-group ${selected ? "selected" : ""}`}>
      <div
        className="user-group-header"
        role="button"
        tabIndex={0}
        onClick={() => onSelectGroup(group.userId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onSelectGroup(group.userId);
        }}
      >
        <div
          className="queue-handle"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          ≡
        </div>

        <div className="queue-index">{groupIndex + 1}</div>

        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = !allSelected && anySelected;
          }}
          onChange={() => onToggleGroupSelected(group.userId)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select all purchases for this user"
          title="Select all"
        />

        <div className="user-group-title">
          <div className="pseudo">{group.pseudo}</div>
          <div className="meta">{group.items.length} item(s) · Total {totalDp} DP</div>
        </div>
      </div>

      <div className="user-group-items">
        {group.items.map((p) => (
          <div
            key={p.id}
            className="user-item"
            role="button"
            tabIndex={0}
            onClick={() => onSelectItem(p.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onSelectItem(p.id);
            }}
          >
            <div className="queue-thumb" aria-hidden="true">
              {getImageUrl(p) ? (
                <img src={getImageUrl(p)} alt="" loading="lazy" />
              ) : (
                <div className="queue-thumb-placeholder" />
              )}
            </div>

            <div className="user-item-main">
              <div className="top">
                <span className="meta">{p.itemName}</span>
              </div>
              <div className="meta">
                Qty {p.quantity} · {p.totalCost} DP{formatPurchaseTime(p) ? ` · ${formatPurchaseTime(p)}` : ""}
              </div>
            </div>

            <div className="queue-actions" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={p.__checked || false}
                onChange={() => onToggleItemSelected(p.id)}
                aria-label="Select purchase"
                title="Select"
              />
              <select
                value={p.status}
                onChange={(e) => onStatusChange(p.userId, p.id, e.target.value)}
                aria-label="Update status"
              >
                <option value={STATUS.Pending}>{STATUS.Pending}</option>
                <option value={STATUS.Shipped}>{STATUS.Shipped}</option>
                <option value={STATUS.Delivered}>{STATUS.Delivered}</option>
                <option value={STATUS.Cancelled}>{STATUS.Cancelled}</option>
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const AdminManagePurchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [activeTab, setActiveTab] = useState("queue"); // queue | all | completed | archive
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedPendingPurchases, setSelectedPendingPurchases] = useState([]);
  const [selectedPaidPurchases, setSelectedPaidPurchases] = useState([]);
  const [selectedArchivedPurchases, setSelectedArchivedPurchases] = useState([]);

  const [activeGroupUserId, setActiveGroupUserId] = useState(null);
  const [activePurchaseId, setActivePurchaseId] = useState(null);
  const [groupOrderUserIds, setGroupOrderUserIds] = useState([]);
  const baselineGroupOrderRef = useRef([]);
  const [queueDirty, setQueueDirty] = useState(false);

  // Cache item thumbnails keyed by `${collection}::${itemId}`
  const [itemImageByKey, setItemImageByKey] = useState({});
  const inflightItemKeys = useRef(new Set());

  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const purchasesData = await fetchAllPurchasesWithPseudo();
        setPurchases(purchasesData);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      user.getIdTokenResult(true).then((idTokenResult) => {
        if (!idTokenResult.claims.admin) {
          toast.info('⚠️ You are not recognized as an admin. Please log out and log back in.');
        }
      }).catch((err) => {
        // Removed: console.error("Error fetching ID token:", err);
      });
    }
  }, []);

  const handleStatusChange = async (userId, purchaseId, newStatus) => {
    await updatePurchaseStatus(userId, purchaseId, newStatus);
    const updated = await fetchAllPurchasesWithPseudo();
    setPurchases(updated);
  };

  const refreshPurchases = async () => {
    setLoading(true);
    try {
      const updated = await fetchAllPurchasesWithPseudo();
      setPurchases(updated);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePendingPurchase = (purchaseId) => {
    setSelectedPendingPurchases(prev => 
      prev.includes(purchaseId) 
        ? prev.filter(id => id !== purchaseId)
        : [...prev, purchaseId]
    );
  };

  const handleTogglePaidPurchase = (purchaseId) => {
    setSelectedPaidPurchases(prev => 
      prev.includes(purchaseId) 
        ? prev.filter(id => id !== purchaseId)
        : [...prev, purchaseId]
    );
  };

  const handleToggleArchivedPurchase = (purchaseId) => {
    setSelectedArchivedPurchases((prev) =>
      prev.includes(purchaseId)
        ? prev.filter((id) => id !== purchaseId)
        : [...prev, purchaseId]
    );
  };

  const archivePurchasesByIds = async (purchaseIds) => {
    if (!purchaseIds || purchaseIds.length === 0) {
      toast.info('Please select purchases to archive.');
      return;
    }

    if (!window.confirm(`Archive ${purchaseIds.length} selected purchase(s)?`)) {
      return;
    }

    try {
      await Promise.all(
        purchaseIds.map(async (purchaseId) => {
          const purchase = purchases.find((p) => p.id === purchaseId);
          if (!purchase) return;
          await updateDoc(doc(db, `users/${purchase.userId}/purchaseHistory/${purchaseId}`), {
            archived: true,
            archivedAt: serverTimestamp(),
          });
        })
      );
      toast.success(`✅ ${purchaseIds.length} purchase(s) archived`);
      await refreshPurchases();
    } catch {
      toast.error('❌ Error archiving purchases.');
    }
  };

  const handleArchivePendingPurchases = async () => {
    const ids = [...selectedPendingPurchases];
    await archivePurchasesByIds(ids);
    setSelectedPendingPurchases([]);
  };

  const handleArchivePaidPurchases = async () => {
    const ids = [...selectedPaidPurchases];
    await archivePurchasesByIds(ids);
    setSelectedPaidPurchases([]);
  };

  const handleDeleteArchivedPurchases = async () => {
    if (selectedArchivedPurchases.length === 0) {
      toast.info('Please select archived purchases to delete.');
      return;
    }

    if (!window.confirm(`Permanently delete ${selectedArchivedPurchases.length} archived purchase(s)? This cannot be undone.`)) {
      return;
    }

    try {
      for (const purchaseId of selectedArchivedPurchases) {
        const purchase = purchases.find((p) => p.id === purchaseId);
        if (!purchase) continue;
        await deleteDoc(doc(db, `users/${purchase.userId}/purchaseHistory/${purchaseId}`));
      }
      toast.success(`✅ ${selectedArchivedPurchases.length} archived purchase(s) deleted!`);
      setSelectedArchivedPurchases([]);
      await refreshPurchases();
    } catch {
      toast.error('❌ Error deleting archived purchases.');
    }
  };

  const filteredPurchases = useMemo(() => {
    const base = purchases
      .filter((p) => !p?.archived)
      .filter((p) => matchesSearch(p, searchQuery));
    if (statusFilter === "All") return base;
    return base.filter((p) => p.status === statusFilter);
  }, [purchases, searchQuery, statusFilter]);

  const archivedPurchases = useMemo(() => {
    const base = purchases
      .filter((p) => !!p?.archived)
      .filter((p) => matchesSearch(p, searchQuery));
    if (statusFilter === "All") return base;
    return base.filter((p) => p.status === statusFilter);
  }, [purchases, searchQuery, statusFilter]);

  const pendingPurchases = useMemo(
    () => filteredPurchases.filter((p) => [STATUS.Pending, STATUS.Cancelled].includes(p.status)),
    [filteredPurchases]
  );
  const paidPurchases = useMemo(
    () => filteredPurchases.filter((p) => [STATUS.Shipped, STATUS.Delivered].includes(p.status)),
    [filteredPurchases]
  );

  // Queue is the full Pending list (search should not change the underlying order)
  const queuePurchasesRaw = useMemo(
    () => purchases.filter((p) => p.status === STATUS.Pending && !p?.archived),
    [purchases]
  );

  const purchaseById = useMemo(() => {
    const map = new Map();
    for (const p of purchases) map.set(p.id, p);
    return map;
  }, [purchases]);

  const kpis = useMemo(() => {
    const counts = { pending: 0, shipped: 0, delivered: 0, cancelled: 0 };
    for (const p of purchases) {
      if (p?.archived) continue;
      if (p.status === STATUS.Pending) counts.pending += 1;
      else if (p.status === STATUS.Shipped) counts.shipped += 1;
      else if (p.status === STATUS.Delivered) counts.delivered += 1;
      else if (p.status === STATUS.Cancelled) counts.cancelled += 1;
    }
    return counts;
  }, [purchases]);

  const pendingGroupsRaw = useMemo(() => {
    const byUser = new Map();
    for (const p of queuePurchasesRaw) {
      if (!p?.userId) continue;
      const existing = byUser.get(p.userId) || { userId: p.userId, pseudo: p.pseudo || p.userId, items: [] };
      existing.pseudo = existing.pseudo || p.pseudo || p.userId;
      existing.items.push(p);
      byUser.set(p.userId, existing);
    }

    for (const g of byUser.values()) {
      g.items.sort((a, b) => {
        const aq = Number.isFinite(a.queuePosition) ? a.queuePosition : 999999;
        const bq = Number.isFinite(b.queuePosition) ? b.queuePosition : 999999;
        if (aq !== bq) return aq - bq;
        const at = toMillis(pickFirstTimeValue(a)) ?? 0;
        const bt = toMillis(pickFirstTimeValue(b)) ?? 0;
        if (at !== bt) return at - bt;
        return String(a.id).localeCompare(String(b.id));
      });
    }

    return Array.from(byUser.values());
  }, [queuePurchasesRaw]);

  useEffect(() => {
    const userIds = pendingGroupsRaw.map((g) => g.userId);
    const userSet = new Set(userIds);

    setGroupOrderUserIds((prev) => {
      if (!prev || prev.length === 0) {
        const sorted = [...pendingGroupsRaw].sort((a, b) => {
          const aFirst = a.items[0];
          const bFirst = b.items[0];
          const aq = aFirst && Number.isFinite(aFirst.queuePosition) ? aFirst.queuePosition : 999999;
          const bq = bFirst && Number.isFinite(bFirst.queuePosition) ? bFirst.queuePosition : 999999;
          if (aq !== bq) return aq - bq;
          const at = toMillis(pickFirstTimeValue(aFirst)) ?? 0;
          const bt = toMillis(pickFirstTimeValue(bFirst)) ?? 0;
          return at - bt;
        });
        const next = sorted.map((g) => g.userId);
        baselineGroupOrderRef.current = next;
        return next;
      }

      const kept = prev.filter((id) => userSet.has(id));
      const newOnes = userIds.filter((id) => !kept.includes(id));
      const next = [...kept, ...newOnes];
      if (!queueDirty) baselineGroupOrderRef.current = next;
      return next;
    });
  }, [pendingGroupsRaw, queueDirty]);

  const pendingGroupByUserId = useMemo(() => {
    const map = new Map();
    for (const g of pendingGroupsRaw) map.set(g.userId, g);
    return map;
  }, [pendingGroupsRaw]);

  const pendingGroupsOrdered = useMemo(() => {
    return groupOrderUserIds
      .map((userId) => pendingGroupByUserId.get(userId))
      .filter(Boolean)
      .map((g) => ({
        ...g,
        items: g.items.map((p) => ({
          ...p,
          __checked: selectedPendingPurchases.includes(p.id),
        })),
      }));
  }, [groupOrderUserIds, pendingGroupByUserId, selectedPendingPurchases]);

  const pendingGroupsVisible = useMemo(() => {
    if (!searchQuery.trim()) return pendingGroupsOrdered;
    const q = searchQuery.trim().toLowerCase();
    return pendingGroupsOrdered
      .map((g) => {
        const userMatches =
          (g.pseudo || "").toLowerCase().includes(q) ||
          (g.userId || "").toLowerCase().includes(q);
        const items = userMatches ? g.items : g.items.filter((p) => matchesSearch(p, searchQuery));
        if (items.length === 0) return null;
        return { ...g, items };
      })
      .filter(Boolean);
  }, [pendingGroupsOrdered, searchQuery]);

  const activePurchase = useMemo(
    () => (activePurchaseId ? purchaseById.get(activePurchaseId) : null),
    [activePurchaseId, purchaseById]
  );

  const getCachedImageUrl = (purchase) => {
    if (!purchase) return null;
    // If someday the purchase record stores it directly, prefer it.
    if (purchase.imageUrl) return purchase.imageUrl;
    const key = getItemKey(purchase);
    if (!key) return null;
    return itemImageByKey[key] || null;
  };

  const loadItemImageIfNeeded = async (purchase) => {
    const key = getItemKey(purchase);
    if (!key) return;
    if (itemImageByKey[key]) return;
    if (inflightItemKeys.current.has(key)) return;

    const ref = getItemDocRef(purchase);
    if (!ref) return;

    inflightItemKeys.current.add(key);
    try {
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() : null;
      const url = pickItemImageUrl(data);
      if (url) {
        setItemImageByKey((prev) => ({ ...prev, [key]: url }));
      }
    } catch {
      // ignore (permissions/network). UI will fall back to placeholder.
    } finally {
      inflightItemKeys.current.delete(key);
    }
  };

  const handleToggleGroupSelected = (userId) => {
    const group = pendingGroupByUserId.get(userId);
    if (!group) return;
    const groupItemIds = group.items.map((p) => p.id);
    const allSelected = groupItemIds.length > 0 && groupItemIds.every((id) => selectedPendingPurchases.includes(id));
    setSelectedPendingPurchases((prev) => {
      const set = new Set(prev);
      if (allSelected) {
        for (const id of groupItemIds) set.delete(id);
      } else {
        for (const id of groupItemIds) set.add(id);
      }
      return Array.from(set);
    });
  };

  // Lazy-fetch thumbnails for the currently visible queue list and the selected purchase.
  useEffect(() => {
    const visibleItems = (pendingGroupsVisible || []).flatMap((g) => g.items || []);
    const toFetch = [];
    for (const p of visibleItems) {
      const key = getItemKey(p);
      if (!key) continue;
      if (itemImageByKey[key]) continue;
      toFetch.push(p);
      if (toFetch.length >= 20) break;
    }

    if (activePurchase) {
      const key = getItemKey(activePurchase);
      if (key && !itemImageByKey[key]) toFetch.unshift(activePurchase);
    }

    if (toFetch.length === 0) return;
    Promise.all(toFetch.map(loadItemImageIfNeeded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGroupsVisible, activePurchaseId]);

  const onDragEnd = (event) => {
    if (searchQuery.trim()) {
      toast.info("Clear search to reorder the queue.");
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setGroupOrderUserIds((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    setQueueDirty(true);
  };

  const saveQueue = async () => {
    const updates = [];
    for (let groupIndex = 0; groupIndex < pendingGroupsOrdered.length; groupIndex += 1) {
      const group = pendingGroupsOrdered[groupIndex];
      for (let itemIndex = 0; itemIndex < group.items.length; itemIndex += 1) {
        const purchase = group.items[itemIndex];
        updates.push({
          userId: purchase.userId,
          purchaseId: purchase.id,
          // Keep items grouped by user, while preserving within-user order.
          queuePosition: (groupIndex + 1) * 1000 + (itemIndex + 1),
        });
      }
    }

    try {
      await updatePurchaseQueuePositions(updates);
      toast.success("✅ Queue order saved");
      setQueueDirty(false);
      baselineGroupOrderRef.current = [...groupOrderUserIds];
      await refreshPurchases();
    } catch (e) {
      toast.error("❌ Failed to save queue order");
    }
  };

  const resetQueue = () => {
    setGroupOrderUserIds(baselineGroupOrderRef.current || []);
    setQueueDirty(false);
    toast.info("Queue order reset");
  };

  const bulkUpdateStatus = async (purchaseIds, newStatus) => {
    if (!purchaseIds || purchaseIds.length === 0) {
      toast.info("Please select purchases first.");
      return;
    }
    try {
      await Promise.all(
        purchaseIds.map((purchaseId) => {
          const purchase = purchaseById.get(purchaseId);
          if (!purchase) return Promise.resolve();
          return updatePurchaseStatus(purchase.userId, purchaseId, newStatus);
        })
      );
      toast.success(`✅ Updated ${purchaseIds.length} purchase(s) to ${newStatus}`);
      if (newStatus !== STATUS.Pending) {
        setSelectedPendingPurchases([]);
      }
      await refreshPurchases();
    } catch {
      toast.error("❌ Bulk update failed");
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-section">
        <div className="purchase-admin-header">
          <h2><PackageIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Manage Purchases</h2>
          <div className="purchase-admin-header-actions">
            <button onClick={() => navigate('/admin-panelx-952504')}>
              ← Back to Dashboard
            </button>
            <button className="purchase-admin-button secondary" onClick={refreshPurchases} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="purchase-admin-toolbar">
        <input
          type="text"
          placeholder="Search by pseudo, item, userId, purchaseId..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="All">All statuses</option>
          <option value={STATUS.Pending}>{STATUS.Pending}</option>
          <option value={STATUS.Shipped}>{STATUS.Shipped}</option>
          <option value={STATUS.Delivered}>{STATUS.Delivered}</option>
          <option value={STATUS.Cancelled}>{STATUS.Cancelled}</option>
        </select>

        <div className="purchase-admin-tabs">
          <button
            className={`purchase-admin-tab ${activeTab === "queue" ? "active" : ""}`}
            onClick={() => setActiveTab("queue")}
          >
            Queue
          </button>
          <button
            className={`purchase-admin-tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            Table
          </button>
          <button
            className={`purchase-admin-tab ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            Completed
          </button>
          <button
            className={`purchase-admin-tab ${activeTab === "archive" ? "active" : ""}`}
            onClick={() => setActiveTab("archive")}
          >
            Archive
          </button>
        </div>
      </div>

      <div className="purchase-admin-kpis">
        <div className="purchase-kpi"><div className="label">Pending</div><div className="value">{kpis.pending}</div></div>
        <div className="purchase-kpi"><div className="label">Shipped</div><div className="value">{kpis.shipped}</div></div>
        <div className="purchase-kpi"><div className="label">Delivered</div><div className="value">{kpis.delivered}</div></div>
        <div className="purchase-kpi"><div className="label">Cancelled</div><div className="value">{kpis.cancelled}</div></div>
      </div>

      {activeTab === "queue" && (
        <div className="purchase-admin-content">
          <div className="purchase-card">
            <div className="purchase-card-title">
              <h3>🧾 Fulfillment Queue (Pending)</h3>
              <div className="purchase-card-actions">
                <button
                  className="purchase-admin-button secondary"
                  onClick={resetQueue}
                  disabled={!queueDirty || !!searchQuery.trim()}
                  title="Reset queue order to last saved order"
                >
                  Reset
                </button>
                <button
                  className="purchase-admin-button"
                  onClick={saveQueue}
                  disabled={!queueDirty || !!searchQuery.trim()}
                  title="Save queue order to Firestore"
                >
                  Save Order
                </button>
              </div>
            </div>

            {searchQuery.trim() ? (
              <div style={{ color: '#bdbdbd', marginBottom: 8 }}>
                Search is active — drag-and-drop is disabled to avoid partial reorders.
              </div>
            ) : null}

            <div className="purchase-card-actions" style={{ marginBottom: 10 }}>
              <button
                className="purchase-admin-button"
                onClick={() => bulkUpdateStatus(selectedPendingPurchases, STATUS.Shipped)}
                disabled={selectedPendingPurchases.length === 0}
              >
                Mark Shipped ({selectedPendingPurchases.length})
              </button>
              <button
                className="purchase-admin-button secondary"
                onClick={() => bulkUpdateStatus(selectedPendingPurchases, STATUS.Cancelled)}
                disabled={selectedPendingPurchases.length === 0}
              >
                Cancel ({selectedPendingPurchases.length})
              </button>
              <button
                className="purchase-admin-button danger"
                onClick={handleArchivePendingPurchases}
                disabled={selectedPendingPurchases.length === 0}
              >
                Archive ({selectedPendingPurchases.length})
              </button>
            </div>

            {pendingGroupsVisible.length === 0 ? (
              <div style={{ color: '#bdbdbd', padding: 12 }}>
                No pending purchases found.
              </div>
            ) : (
              <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={pendingGroupsVisible.map((g) => g.userId)} strategy={verticalListSortingStrategy}>
                  <div className="queue-list">
                    {pendingGroupsVisible.map((g, visibleIndex) => {
                      const idx = groupOrderUserIds.indexOf(g.userId);
                      return (
                        <SortableUserGroup
                          key={g.userId}
                          group={g}
                          groupIndex={idx >= 0 ? idx : visibleIndex}
                          selected={activeGroupUserId === g.userId}
                          onSelectGroup={(userId) => {
                            setActiveGroupUserId(userId);
                            setActivePurchaseId(null);
                          }}
                          onSelectItem={(purchaseId) => {
                            setActivePurchaseId(purchaseId);
                            const p = purchaseById.get(purchaseId);
                            if (p?.userId) setActiveGroupUserId(p.userId);
                          }}
                          onToggleItemSelected={handleTogglePendingPurchase}
                          onToggleGroupSelected={handleToggleGroupSelected}
                          onStatusChange={handleStatusChange}
                          getImageUrl={getCachedImageUrl}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          <div className="purchase-card">
            <div className="purchase-card-title">
              <h3>Details</h3>
              <div className="purchase-card-actions">
                {activePurchase && (
                  <button
                    className="purchase-admin-button secondary"
                    onClick={() => setActivePurchaseId(null)}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>

            {!activePurchase ? (
              <div style={{ color: '#bdbdbd', padding: 12 }}>
                Select a purchase from the queue to view details.
              </div>
            ) : (
              <div className="details-grid">
                <div className="details-image">
                  {getCachedImageUrl(activePurchase) ? (
                    <img src={getCachedImageUrl(activePurchase)} alt={activePurchase.itemName || 'Item'} loading="lazy" />
                  ) : (
                    <div className="details-image-placeholder">No image</div>
                  )}
                </div>
                <div className="details-row"><div className="k">Pseudo</div><div className="v">{activePurchase.pseudo}</div></div>
                <div className="details-row"><div className="k">Item</div><div className="v">{activePurchase.itemName}</div></div>
                <div className="details-row"><div className="k">Quantity</div><div className="v">{activePurchase.quantity}</div></div>
                <div className="details-row"><div className="k">Total DP</div><div className="v">{activePurchase.totalCost}</div></div>
                <div className="details-row"><div className="k">Status</div><div className="v">{activePurchase.status}</div></div>
                <div className="details-row"><div className="k">Purchased</div><div className="v">{formatPurchaseTime(activePurchase) || "—"}</div></div>
                <div className="details-row"><div className="k">User ID</div><div className="v">{activePurchase.userId}</div></div>
                <div className="details-row"><div className="k">Purchase ID</div><div className="v">{activePurchase.id}</div></div>

                <div className="purchase-card-actions" style={{ marginTop: 10 }}>
                  <button
                    className="purchase-admin-button"
                    onClick={() => handleStatusChange(activePurchase.userId, activePurchase.id, STATUS.Shipped)}
                  >
                    Mark Shipped
                  </button>
                  <button
                    className="purchase-admin-button secondary"
                    onClick={() => handleStatusChange(activePurchase.userId, activePurchase.id, STATUS.Delivered)}
                  >
                    Mark Delivered
                  </button>
                  <button
                    className="purchase-admin-button secondary"
                    onClick={() => handleStatusChange(activePurchase.userId, activePurchase.id, STATUS.Cancelled)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "archive" && (
        <div className="purchase-columns">
          <div className="column" style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3>🗃️ Archive</h3>
              <button
                onClick={handleDeleteArchivedPurchases}
                disabled={selectedArchivedPurchases.length === 0}
                className="purchase-admin-button danger"
                title="Permanently delete archived purchases"
              >
                Delete Selected ({selectedArchivedPurchases.length})
              </button>
            </div>

            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedArchivedPurchases.length === archivedPurchases.length && archivedPurchases.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedArchivedPurchases(archivedPurchases.map((p) => p.id));
                        } else {
                          setSelectedArchivedPurchases([]);
                        }
                      }}
                    />
                  </th>
                  <th>Pseudo</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Total DP</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {archivedPurchases.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedArchivedPurchases.includes(p.id)}
                        onChange={() => handleToggleArchivedPurchase(p.id)}
                      />
                    </td>
                    <td>{p.pseudo}</td>
                    <td>{p.itemName}</td>
                    <td>{p.quantity}</td>
                    <td>{p.totalCost}</td>
                    <td>{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab !== "queue" && activeTab !== "archive" && (
        <div className="purchase-columns">
          <div className="column">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3>🕒 Pending / Cancelled</h3>
              <button 
                onClick={handleArchivePendingPurchases}
                disabled={selectedPendingPurchases.length === 0}
                className="purchase-admin-button secondary"
              >
                Archive Selected ({selectedPendingPurchases.length})
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedPendingPurchases.length === pendingPurchases.length && pendingPurchases.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPendingPurchases(pendingPurchases.map(p => p.id));
                        } else {
                          setSelectedPendingPurchases([]);
                        }
                      }}
                    />
                  </th>
                  <th>Pseudo</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Total DP</th>
                  <th>Status</th>
                  <th>Update</th>
                </tr>
              </thead>
              <tbody>
                {pendingPurchases
                  .sort((a, b) => {
                    if (a.status === STATUS.Pending && b.status !== STATUS.Pending) return -1;
                    if (b.status === STATUS.Pending && a.status !== STATUS.Pending) return 1;
                    return 0;
                  })
                  .map((p) => (
                    <tr key={p.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPendingPurchases.includes(p.id)}
                          onChange={() => handleTogglePendingPurchase(p.id)}
                        />
                      </td>
                      <td>{p.pseudo}</td>
                      <td>{p.itemName}</td>
                      <td>{p.quantity}</td>
                      <td>{p.totalCost}</td>
                      <td>{p.status}</td>
                      <td>
                        <select
                          value={p.status}
                          onChange={(e) => handleStatusChange(p.userId, p.id, e.target.value)}
                        >
                          <option value={STATUS.Pending}>{STATUS.Pending}</option>
                          <option value={STATUS.Shipped}>{STATUS.Shipped}</option>
                          <option value={STATUS.Delivered}>{STATUS.Delivered}</option>
                          <option value={STATUS.Cancelled}>{STATUS.Cancelled}</option>
                        </select>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="column">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3>✅ Completed (Shipped / Delivered)</h3>
              <button 
                onClick={handleArchivePaidPurchases}
                disabled={selectedPaidPurchases.length === 0}
                className="purchase-admin-button secondary"
              >
                Archive Selected ({selectedPaidPurchases.length})
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedPaidPurchases.length === paidPurchases.length && paidPurchases.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPaidPurchases(paidPurchases.map(p => p.id));
                        } else {
                          setSelectedPaidPurchases([]);
                        }
                      }}
                    />
                  </th>
                  <th>Pseudo</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Total DP</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === "completed" ? paidPurchases : paidPurchases).map((p) => (
                  <tr key={p.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedPaidPurchases.includes(p.id)}
                        onChange={() => handleTogglePaidPurchase(p.id)}
                      />
                    </td>
                    <td>{p.pseudo}</td>
                    <td>{p.itemName}</td>
                    <td>{p.quantity}</td>
                    <td>{p.totalCost}</td>
                    <td>{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagePurchases; 